import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useWallet } from "../context/WalletContext";
import { Button } from "../components/ui/button";

export function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const { refresh } = useWallet();
  const navigate = useNavigate();
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.paymentStatus(sid);
        if (!active) return;
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status) || tries.current > 12) { setStatus("error"); return; }
      } catch { if (tries.current > 12) { setStatus("error"); return; } }
      tries.current += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [refresh]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
      {status === "checking" && (<><Loader2 className="h-12 w-12 animate-spin text-celeste-or" /><p className="mt-4 text-celeste-lilas">Confirmation du paiement…</p></>)}
      {status === "paid" && (
        <div className="glass rounded-3xl border border-celeste-or/25 p-8" data-testid="payment-success">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
          <h1 className="mt-4 font-serif text-2xl text-celeste-ivoire">Crédits ajoutés !</h1>
          <p className="mt-2 text-sm text-celeste-lilas/75">Votre portefeuille a été rechargé. Vous pouvez démarrer une consultation.</p>
          <div className="mt-6 flex gap-3">
            <Button className="flex-1 gold-btn font-semibold" onClick={() => navigate("/experts")}>Voir les experts</Button>
            <Button variant="ghost" className="flex-1 border border-celeste-violet/30 text-celeste-lilas" onClick={() => navigate("/credits")}>Mon portefeuille</Button>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="glass rounded-3xl border border-red-400/25 p-8" data-testid="payment-error">
          <XCircle className="mx-auto h-14 w-14 text-red-400" />
          <h1 className="mt-4 font-serif text-2xl text-celeste-ivoire">Paiement non confirmé</h1>
          <p className="mt-2 text-sm text-celeste-lilas/75">Si vous avez été débité, le crédit apparaîtra sous peu. Sinon, réessayez.</p>
          <Button className="mt-6 gold-btn font-semibold" onClick={() => navigate("/credits")}>Retour au portefeuille</Button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
      <div className="glass rounded-3xl border border-celeste-violet/25 p-8" data-testid="payment-cancel">
        <XCircle className="mx-auto h-14 w-14 text-celeste-lilas" />
        <h1 className="mt-4 font-serif text-2xl text-celeste-ivoire">Paiement annulé</h1>
        <p className="mt-2 text-sm text-celeste-lilas/75">Aucun montant n'a été prélevé.</p>
        <Button className="mt-6 gold-btn font-semibold" onClick={() => navigate("/credits")}>Retour au portefeuille</Button>
      </div>
    </div>
  );
}
